import * as React from 'react';
import cx from 'classnames';
import s from './ProductList.scss';
import {Experiments, GalleryGridItemType} from '../../../constants';
import {GridType} from '../../../types/galleryTypes';
import type {GalleryGridItem, IProduct, PromotionGridItem} from '../../../types/galleryTypes';
import {Announcer} from '@wix/wixstores-client-core/dist/es/src/a11y/announcer';
import {IGalleryGlobalProps} from '../../galleryGlobalStrategy';
import {FadeIn} from './FadeIn/FadeIn';
import {withGlobals} from '../../../globalPropsContext';
import {GridItem} from './GridItem';

export interface ProductListGridProps extends IGalleryGlobalProps {
  focusAt: number;
  gridType: GridType;
  isMobileOverride: boolean;
  forceSingleColumn: boolean;
  items: GalleryGridItem[];
  products: IProduct[];
  renderKey: string;
  ProductItem: Function;
  editableGridTemplateRepeatOption?: boolean;
  shouldShowLoadMoreButton?: boolean;
}

export enum ProductListGridDataHook {
  List = 'product-list-wrapper',
  Item = 'product-list-grid-item',
}

export class ProductListGridComponent extends React.Component<ProductListGridProps> {
  private readonly itemRefs = [];
  private a11yAnnouncer: Announcer;

  public static defaultProps = {
    focusAt: -1,
    gridType: GridType.MANUAL,
    isMobileOverride: false,
    forceSingleColumn: false,
    items: [],
    products: [],
    renderKey: 'string',
    shouldShowLoadMoreButton: true,
  };

  public componentDidMount() {
    this.a11yAnnouncer = new Announcer('gallery-list-announcer');
    if (this.props.globals.useExperiments.enabled(Experiments.ShowPromotionsInGallery)) {
      this.reportRenderedPromotionImpressions();
    }
  }

  public componentWillUnmount() {
    this.a11yAnnouncer.cleanup();
  }

  public componentDidUpdate(prevProps: ProductListGridProps) {
    if (this.props.focusAt !== prevProps.focusAt) {
      this.focusItem(this.props.focusAt);
    }
  }

  private focusItem(i) {
    this.itemRefs[i] && this.itemRefs[i].focus && this.itemRefs[i].focus();
  }

  private reportRenderedPromotionImpressions() {
    const renderedPromotions = this.props.items
      .filter((item): item is PromotionGridItem => item?.type === GalleryGridItemType.Promotion)
      .map((item) => item.data);
    if (renderedPromotions.length === 0) {
      return;
    }

    this.props.globals.reportRenderedPromotionImpressions(renderedPromotions);
  }

  public render() {
    const {
      items,
      products,
      isMobileOverride,
      forceSingleColumn,
      gridType,
      renderKey,
      ProductItem,
      editableGridTemplateRepeatOption,
    } = this.props;
    const showPromotionsInGalleryEnabled = this.props.globals.useExperiments.enabled(
      Experiments.ShowPromotionsInGallery
    );
    const classNames = cx(s.grid, {
      [s.editableGridTemplateRepeatOption]: editableGridTemplateRepeatOption,
      [s.autoGrid]: gridType === GridType.AUTO,
      [s.manualGrid]: gridType !== GridType.AUTO,
      [s.mobile]: isMobileOverride,
      [s.forceSingleColumn]: forceSingleColumn,
      [s.loadMoreButtonIsShowing]: this.props.shouldShowLoadMoreButton,
    });

    return (
      <ul className={classNames} data-hook={ProductListGridDataHook.List} data-grid-type={gridType}>
        {showPromotionsInGalleryEnabled
          ? items.map((gridItem, index) => {
              const id =
                gridItem?.type === GalleryGridItemType.Product ? gridItem.data.id : gridItem?.data?.promotion?.id;
              const keyId = id || `grid-item-${index}`;
              return (
                <li key={`${keyId}-${renderKey}`} data-hook={ProductListGridDataHook.Item}>
                  {gridItem && (
                    <FadeIn>
                      <GridItem
                        gridItem={gridItem}
                        index={index}
                        ProductItem={ProductItem}
                        setItemRef={(itemIndex, ref) => (this.itemRefs[itemIndex] = ref)}
                        onAnnounce={(content) => this.a11yAnnouncer.announce(content)}
                        onPromotionItemClick={this.props.globals.handlePromotionItemClick}
                      />
                    </FadeIn>
                  )}
                </li>
              );
            })
          : products.map((product, index) => {
              const id = product ? product.id : `product-${index}`;
              return (
                <li key={`${id}-${index}-${renderKey}`} data-hook={ProductListGridDataHook.Item}>
                  {product && (
                    <FadeIn>
                      <ProductItem
                        index={index}
                        product={product}
                        innerRef={(ref) => (this.itemRefs[index] = ref)}
                        announce={(content) => this.a11yAnnouncer.announce(content)}
                      />
                    </FadeIn>
                  )}
                </li>
              );
            })}
      </ul>
    );
  }
}

export const ProductListGrid = withGlobals(ProductListGridComponent);
